import _extends from "@babel/runtime/helpers/esm/extends";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _map from "lodash/map";
import _filter from "lodash/filter";
import _invoke from "lodash/invoke";
import { menuBehavior } from '@stardust-ui/accessibility';
import * as customPropTypes from '@stardust-ui/react-proptypes';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { AutoControlledComponent, childrenExist, createShorthandFactory, commonPropTypes, getKindProp, rtlTextContainer } from '../../lib';
import { mergeComponentVariables } from '../../lib/mergeThemes';
import MenuItem from './MenuItem';
import { withSafeTypeForAs } from '../../types';
import MenuDivider from './MenuDivider';

var Menu =
/*#__PURE__*/
function (_AutoControlledCompon) {
  _inherits(Menu, _AutoControlledCompon);

  function Menu() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, Menu);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(Menu)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "handleItemOverrides", function (variables) {
      return function (predefinedProps) {
        return {
          onClick: function onClick(e, itemProps) {
            var index = itemProps.index;

            _this.setState({
              activeIndex: index
            });

            _invoke(_this.props, 'onItemClick', e, itemProps);

            _invoke(predefinedProps, 'onClick', e, itemProps);
          },
          onActiveChanged: function onActiveChanged(e, props) {
            var index = props.index,
                active = props.active;

            if (active) {
              _this.setState({
                activeIndex: index
              });
            } else if (_this.state.activeIndex === index) {
              _this.setState({
                activeIndex: null
              });
            }

            _invoke(predefinedProps, 'onActiveChanged', e, props);
          },
          variables: mergeComponentVariables(variables, predefinedProps.variables)
        };
      };
    });

    _defineProperty(_assertThisInitialized(_this), "handleDividerOverrides", function (variables) {
      return function (predefinedProps) {
        return {
          variables: mergeComponentVariables(variables, predefinedProps.variables)
        };
      };
    });

    _defineProperty(_assertThisInitialized(_this), "renderItems", function (styles, variables, accessibility) {
      var _this$props = _this.props,
          iconOnly = _this$props.iconOnly,
          items = _this$props.items,
          pills = _this$props.pills,
          pointing = _this$props.pointing,
          primary = _this$props.primary,
          secondary = _this$props.secondary,
          underlined = _this$props.underlined,
          vertical = _this$props.vertical,
          submenu = _this$props.submenu,
          indicator = _this$props.indicator;
      var activeIndex = _this.state.activeIndex;

      var itemsCount = _filter(items, function (item) {
        return getKindProp(item, 'item') !== 'divider';
      }).length;

      var itemPosition = 0;

      var overrideItemProps = _this.handleItemOverrides(variables);

      var overrideDividerProps = _this.handleDividerOverrides(variables);

      return _map(items, function (item, index) {
        var active = (typeof activeIndex === 'string' ? parseInt(activeIndex, 10) : activeIndex) === index;
        var kind = getKindProp(item, 'item');

        if (kind === 'divider') {
          return MenuDivider.create(item, {
            defaultProps: function defaultProps() {
              return {
                className: Menu.slotClassNames.divider,
                primary: primary,
                secondary: secondary,
                vertical: vertical,
                styles: styles.divider,
                inSubmenu: submenu,
                accessibility: accessibility.childBehaviors ? accessibility.childBehaviors.divider : undefined
              };
            },
            overrideProps: overrideDividerProps
          });
        }

        itemPosition++;
        return MenuItem.create(item, {
          defaultProps: function defaultProps() {
            return {
              className: Menu.slotClassNames.item,
              iconOnly: iconOnly,
              pills: pills,
              pointing: pointing,
              primary: primary,
              secondary: secondary,
              underlined: underlined,
              vertical: vertical,
              index: index,
              itemPosition: itemPosition,
              itemsCount: itemsCount,
              active: active,
              inSubmenu: submenu,
              indicator: indicator,
              accessibility: accessibility.childBehaviors ? accessibility.childBehaviors.item : undefined
            };
          },
          overrideProps: overrideItemProps
        });
      });
    });

    return _this;
  }

  _createClass(Menu, [{
    key: "renderComponent",
    value: function renderComponent(_ref) {
      var ElementType = _ref.ElementType,
          classes = _ref.classes,
          accessibility = _ref.accessibility,
          styles = _ref.styles,
          variables = _ref.variables,
          unhandledProps = _ref.unhandledProps;
      var children = this.props.children;
      return React.createElement(ElementType, _extends({}, accessibility.attributes.root, rtlTextContainer.getAttributes({
        forElements: [children]
      }), unhandledProps, {
        className: classes.root
      }), childrenExist(children) ? children : this.renderItems(styles, variables, accessibility));
    }
  }]);

  return Menu;
}(AutoControlledComponent);

_defineProperty(Menu, "displayName", 'Menu');

_defineProperty(Menu, "className", 'ui-menu');

_defineProperty(Menu, "slotClassNames", {
  divider: "".concat(Menu.className, "__divider"),
  item: "".concat(Menu.className, "__item")
});

_defineProperty(Menu, "create", void 0);

_defineProperty(Menu, "propTypes", Object.assign({}, commonPropTypes.createCommon({
  content: false
}), {
  activeIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  defaultActiveIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fluid: PropTypes.bool,
  iconOnly: PropTypes.bool,
  items: customPropTypes.collectionShorthandWithKindProp(['divider', 'item']),
  onItemClick: PropTypes.func,
  pills: PropTypes.bool,
  pointing: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['start', 'end'])]),
  primary: customPropTypes.every([customPropTypes.disallow(['secondary']), PropTypes.bool]),
  secondary: customPropTypes.every([customPropTypes.disallow(['primary']), PropTypes.bool]),
  underlined: PropTypes.bool,
  vertical: PropTypes.bool,
  submenu: PropTypes.bool,
  indicator: customPropTypes.itemShorthandWithoutJSX
}));

_defineProperty(Menu, "defaultProps", {
  as: 'ul',
  accessibility: menuBehavior
});

_defineProperty(Menu, "autoControlledProps", ['activeIndex']);

_defineProperty(Menu, "Item", MenuItem);

_defineProperty(Menu, "Divider", MenuDivider);

Menu.create = createShorthandFactory({
  Component: Menu,
  mappedArrayProp: 'items'
});
/**
 * A Menu is a component that offers a grouped list of choices to the user.
 *
 * @accessibility
 * Implements ARIA [Menu](https://www.w3.org/TR/wai-aria-practices-1.1/#menu), [Toolbar](https://www.w3.org/TR/wai-aria-practices-1.1/#toolbar) or [Tabs](https://www.w3.org/TR/wai-aria-practices-1.1/#tabpanel) design pattern, depending on the behavior used.
 * @accessibilityIssues
 * [JAWS - navigation instruction for menubar](https://github.com/FreedomScientific/VFO-standards-support/issues/203)
 * [JAWS - navigation instruction for menu with aria-orientation="horizontal"](https://github.com/FreedomScientific/VFO-standards-support/issues/204)
 * [JAWS [VC] doesn't narrate menu item, when it is open from menu button](https://github.com/FreedomScientific/VFO-standards-support/issues/324)
 * [JAWS [app mode] focus moves to second menu item, when it is open from menu button](https://github.com/FreedomScientific/VFO-standards-support/issues/325)
 * [Enter into a tablist JAWS narrates: To switch pages, press Control+PageDown](https://github.com/FreedomScientific/VFO-standards-support/issues/337)
 * 51114083 VoiceOver+Web narrate wrong position in menu / total count of menu items, when pseudo element ::after or ::before is used
 */

export default withSafeTypeForAs(Menu);